import { useState } from 'react'
import type { ChangeEvent } from 'react'
import type { SelectOption } from '@lightspeed/unified-components-helios-theme/react'
import { useT } from '@transifex/react'
import { CONTACT_OPTIONS, CUSTOMER_NAME_FIELD } from '../../../../common/types'
import {
  getDefaultContactOption,
  hasContactInfo,
  isInvalidEmail,
} from '../../../../common/customer-contact-utils'
import { FulfilmentType } from '../../../../../../../models/fulfilment'
import { STEPS } from '../../types'
import type { Step2AddProps } from './Step2Add'

const createInputChangeEvent = (
  name: string,
  value: string
): ChangeEvent<HTMLInputElement> =>
  ({
    preventDefault: () => undefined,
    target: {
      name,
      value,
    },
    currentTarget: {
      name,
      value,
    },
  } as ChangeEvent<HTMLInputElement>)

const shouldDisableNext = (props: Step2AddProps) => {
  const customer = props.newCustomer
  const name: string[] = []
  let hasAllNeededCustomerInfo = false

  if (customer) {
    Object.values(CUSTOMER_NAME_FIELD).forEach(option => {
      const value = customer[option]
      if (value && value.trim()) {
        name.push(option)
      }
    })
    if (hasContactInfo(customer) && name.length > 0) {
      hasAllNeededCustomerInfo = true
    }
  }

  return !hasAllNeededCustomerInfo || isInvalidEmail(customer)
}

export const useStep2AddModel = (props: Step2AddProps) => {
  const t = useT()
  const [contactOption, setContactOption] = useState<CONTACT_OPTIONS>(
    getDefaultContactOption(props.newCustomer)
  )

  const handleInputChange = (name: string, value: string) => {
    props.onChange(createInputChangeEvent(name, value))
  }

  const contactOptions = [
    {
      label: t('Mobile', {
        _context: 'Label for mobile contact option',
      }),
      value: CONTACT_OPTIONS.MOBILE,
    },
    {
      label: t('Phone', {
        _context: 'Label for phone contact option',
      }),
      value: CONTACT_OPTIONS.PHONE,
    },
    {
      label: t('Fax', {
        _context: 'Label for fax contact option',
      }),
      value: CONTACT_OPTIONS.FAX,
    },
  ]

  return {
    backLabel: t('Back', {
      _context: 'Mark as unfulfilled modal - back button',
    }),
    contactNumber: props.newCustomer?.[contactOption] || '',
    contactNumberLabel: t('Contact number', {
      _context: 'Mark as unfulfilled modal - contact number label',
    }),
    contactNumberPlaceholder: t('Enter number', {
      _context: 'Input placeholder text for Contact Number input',
    }),
    contactOption,
    contactOptions,
    description: t('Enter details to contact the customer about this order.'),
    emailLabel: t('Email', {
      _context: 'Mark as unfulfilled modal - email label',
    }),
    emailPlaceholder: t('Enter email', {
      _context: 'Placeholder for email input in customer step',
    }),
    firstNameLabel: t('First name', {
      _context: 'Mark as unfulfilled modal - first name label',
    }),
    firstNamePlaceholder: t('Enter first name', {
      _context: 'Placeholder for first name input in customer step',
    }),
    handleBack: () => props.onStepChange(STEPS.CUSTOMER),
    handleContactNumberChange: (value: string) => {
      handleInputChange(contactOption, value)
    },
    handleContactOptionChange: (option: SelectOption | undefined) => {
      if (option) {
        setContactOption(option.value as CONTACT_OPTIONS)
      }
    },
    handleInputChange,
    handleNext: () => props.onStepChange(STEPS.DETAILS),
    isEmailInvalid: isInvalidEmail(props.newCustomer),
    isNextDisabled: shouldDisableNext(props),
    lastNameLabel: t('Last name', {
      _context: 'Mark as unfulfilled modal - last name label',
    }),
    lastNamePlaceholder: t('Enter last name', {
      _context: 'Placeholder for last name input in customer step',
    }),
    nextLabel: t('Next', {
      _context: 'Mark as unfulfilled modal - next button',
    }),
    title:
      props.type === FulfilmentType.DELIVERY
        ? t('Contact info for the delivery', {
            _context: 'Mark all as unfulfilled modal',
          })
        : t('Contact info for the pickup', {
            _context: 'Mark all as unfulfilled modal',
          }),
  }
}
